/* eslint-disable no-unused-vars, max-params */
import React, { useState, useEffect, useRef } from 'react';
import { IntlProvider } from 'react-intl';
import {
  Alert, DatePicker, DefaultStyle,
  dlsBrightBlue, dlsBrightBlueBg,
  GlyphInfo, Input,
  Label,
  ProgressCircle,
  Select,
  SelectOption,
  Tooltip,
  TooltipButton,
} from '@americanexpress/dls-react';
import { useFetchye } from 'fetchye';
import PropTypes from 'prop-types';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import { compose } from 'redux';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { connect, useSelector } from 'react-redux';
import moment from 'moment';

/* eslint complexity: ["error", 14] */
function DocUploadForm(props) {
  const {
    docTypeId,
    onFormChange,
    languageData,
    scrollIntoForm,
    locale,
    dynamicKey,
    optionalType,
  } = props;
  // Declare a new state variable, which we'll call "count"
  const [displayData, setDisplayData] = useState({});
  const [fieldsData, setFieldsData] = useState({});
  const [validationMessage, setValidationMessage] = useState({});
  const [formHiddenData, setFormHiddenData] = useState({});
  const [inacDocTypeId, setInacDocTypeId] = useState('');
  const [activeDocTypeId, setActiveDocTypeId] = useState('initial');
  const [formData, setFormData] = useState(null);

  const selectedField = useRef(null);
  const prevScrollIntoForm = useRef();

  const inboundURL = useSelector((state) => state.getIn(['config', 'imageViewerDocUploadApiUrl']));
  const {
    data,
    error: formDataError,
    isLoading: formIsLoading,
  } = useFetchye(`${inboundURL}/servicing/v1/communications/docindexform/${docTypeId}`, {
    // include defer here
    credentials: 'include',
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      dynamicKey: `${dynamicKey}`,
    },
  });

  useEffect(() => {
    prevScrollIntoForm.current = scrollIntoForm;
  }, [scrollIntoForm]);

  useEffect(() => {
    /* istanbul ignore next */
    if (prevScrollIntoForm.current !== scrollIntoForm && scrollIntoForm !== null) {
      /* istanbul ignore next */
      selectedField.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [prevScrollIntoForm.current]);

  const transformData = (obj) => {
    let propsStr = ' ';
    obj.propsDa.map((e) => {
      propsStr += `"${e.key}": "${e.value}",`;
      return propsStr;
    });
    propsStr = `{${propsStr.slice(0, Math.max(0, propsStr.length - 1))}}`;
    const options = [{ value: '', label: 'Select' }];
    if (obj.optionsDa && obj.optionsDa.length > 0) {
      obj.optionsDa.map((e) => options.push({ value: e.key, label: e.value }));
    }
    return {
      id: obj.id,
      name: obj.name,
      value: obj.value,
      component: obj.component,
      options,
      props: JSON.parse(propsStr.replace(/\\/g, '\\\\')),
    };
  };

  const isFormValid = () => {
    let validArr = [false];
    // Checks for mandatory fields
    if (formData !== null && formData.body.fieldDa) {
      validArr = formData.body.fieldDa.map((e) => {
        const obj = transformData(e);
        if (obj.component !== 'hidden') {
          if (fieldsData[obj.name] === '') {
            return obj.props.mandatory !== 'true';
          } return validationMessage[`isValid${obj.name}`] === '';
        }
        return true;
      });
    }
    return validArr.find((e) => e === false) === undefined;
  };

  useEffect(() => {
    if (data !== undefined && data.body) {
      setFormData(data);
    }
  }, [data]);

  useEffect(() => {
    const isValidForm = isFormValid();
    if (onFormChange) {
      onFormChange({
        displayData,
        formHiddenData,
        fieldsData,
        validationMessage,
        isValidForm,
      });
    }
  }, [formHiddenData, displayData, fieldsData]);

  useEffect(() => {
    let initialStateValues = {
      displayData,
      formHiddenData,
      activeDocTypeId,
    };

    if (docTypeId !== activeDocTypeId) {
      initialStateValues = {
        displayData: {},
        fieldsData: {},
        validationMessage: {},
        formHiddenData: {},
        activeDocTypeId: docTypeId === '' ? 'initial' : docTypeId,
      };
    }
    if (formData !== null && formData.body.fieldDa) {
      formData.body.fieldDa.map((obj) => {
        if (obj.component === 'hidden') {
          initialStateValues = {
            ...initialStateValues,
            formHiddenData: {
              ...initialStateValues.formHiddenData,
              [obj.name]: obj.value,
            },
          };
        } else if (obj.component === 'from_props') {
          initialStateValues = {
            ...initialStateValues,
            formHiddenData: {
              ...initialStateValues.formHiddenData,
              // eslint-disable-next-line react/destructuring-assignment
              [obj.name]: props[obj.name],
            },
          };
        } else {
          initialStateValues = {
            ...initialStateValues,
            fieldsData: {
              ...initialStateValues.fieldsData,
              [obj.name]: '',
            },
            validationMessage: {
              ...initialStateValues.validationMessage,
              [`isValid${obj.name}`]: '',
            },
          };
        }
        return null;
      });
    }
    if (formData !== null && formData.body.displayProps) {
      const { displayProps } = formData.body;
      let propsStr = ' ';
      displayProps.map((e) => {
        propsStr += `"${e.key}": "${e.value}",`;
        return propsStr;
      });
      const displayPropsData = JSON.parse(`{${propsStr.slice(0, Math.max(0, propsStr.length - 1))}}`);
      initialStateValues = {
        ...initialStateValues,
        displayData: displayPropsData,
      };
    }
    if (activeDocTypeId === 'initial' || docTypeId !== activeDocTypeId) {
      setActiveDocTypeId(initialStateValues.activeDocTypeId);
    }
    setFieldsData(initialStateValues.fieldsData);
    setDisplayData(initialStateValues.displayData);
    setFormHiddenData(initialStateValues.formHiddenData);
    setValidationMessage(initialStateValues.validationMessage);
  }, [formData]);

  const onInputChange = (inputValue, obj) => {
    const regex = obj.props.validation ? obj.props.validation : '^[a-zA-Z0-9 ]*$';
    let alertMessage = '';
    if (obj.props.mandatory === 'true') {
      alertMessage = !inputValue.match(regex) || (inputValue.trim().length === 0) ? obj.props.validationMsg || 'Required field' : '';
    } else {
      alertMessage = !inputValue.match(regex) && (inputValue.trim().length > 0) ? obj.props.validationMsg || 'Required field' : '';
    }
    setFieldsData({
      ...fieldsData,
      [obj.name]: inputValue,
    });
    setValidationMessage({
      ...validationMessage,
      [`isValid${obj.name}`]: alertMessage,
    });
  };

  const onDropdownChange = (e, key, isRequired, validationMsg) => {
    setFieldsData({
      ...fieldsData,
      [key]: e,
    });
    setValidationMessage({
      ...validationMessage,
      [`isValid${key}`]: isRequired && (e.length === 0) ? validationMsg || 'Required field' : '',
    });
  };

  const getDateRange = (numberOdDays, defaultValue, year, type, hasFixedDate) => {
    if (numberOdDays) {
      const days = Number.parseInt(numberOdDays, 10);
      if (!Number.isInteger(days)) return null;
      /* istanbul ignore next */
      let formattedDay = days < 0 ? moment().subtract(Math.abs(days), 'days').format('YYYY-MM-DD') : moment().add(days, 'days').format('YYYY-MM-DD');
      if (year) {
        if (type === 'maxdate' && hasFixedDate === 'true') {
          formattedDay = moment().set({ year: 9999, month: 11, date: 31 }).format('YYYY-MM-DD');
        } else {
          formattedDay = year < 0 ? moment().subtract(Math.abs(year), 'years').format('YYYY-MM-DD') : moment().add(year, 'years').format('YYYY-MM-DD');
        }
      }
      return formattedDay;
    }
    return defaultValue;
  };

  const renderAlert = (isEmptyOrValidMessage, obj) => {
    if ((scrollIntoForm !== null && scrollIntoForm !== undefined) || !isEmptyOrValidMessage) {
      return (
        <Alert
          id="input-3-alert"
          showStateIcon={(validationMessage !== undefined && Object.keys(validationMessage).length === 0) || (validationMessage !== undefined && validationMessage[`isValid${obj.name}`] === '')}
        >
          {validationMessage && validationMessage[`isValid${obj.name}`]}
        </Alert>
      );
    }
    return null;
  };

  const renderMandatory = (obj) => {
    if (optionalType === 'asterisk') {
      if ((obj !== null && obj !== undefined) && obj.props.mandatory === 'true') {
        return <Label theme={{ color: 'red' }}>*</Label>;
      }
    } else if (obj.props.mandatory !== 'true') {
      return `(${languageData.Optional})`;
    }
    return '';
  };
  const renderTooltip = (obj) => (obj.props.tooltip !== '')
    && (
      <Tooltip
        id="info"
        className="margin-1-l"
        triggerElement={(
          <TooltipButton
            aria-label="More Info"
            icon={<GlyphInfo theme={dlsBrightBlue} />}
          />
        )}
        placement="right"
        theme={{ 'div[role=tooltip]': dlsBrightBlueBg }}
      >
        {obj.props.tooltip}
      </Tooltip>
    );

  const renderInput = (obj) => {
    const isEmptyOrValidMessage = validationMessage !== undefined && (validationMessage[`isValid${obj.name}`] === undefined || validationMessage[`isValid${obj.name}`] === '');
    return (
      <div>
        <Label data-testid={obj.name} className="margin-1-t" htmlFor={obj.name}>
          {`${obj.props.label || obj.props.placeholder} `}
          {renderMandatory(obj)}
        </Label>
        {obj.props.tooltip && renderTooltip(obj)}
        <Input
          id={obj.name}
          name={obj.name}
          placeholder={obj.props.placeholder || null}
          showStateIcon={!isEmptyOrValidMessage}
          status={isEmptyOrValidMessage && scrollIntoForm === null ? 'default' : 'error'}
          ref={selectedField}
          data-testid={`${obj.name}InputField`}
          required={obj.props.mandatory === 'true'}
          aria-required={obj.props.mandatory === 'true'}
          value={fieldsData && fieldsData[obj.name] !== undefined && fieldsData[obj.name] !== '' ? fieldsData[obj.name] : obj.props.prefix || ''}
          onChange={(e) => {
            onInputChange(e.target.value, obj);
          }}
        />
        {renderAlert(isEmptyOrValidMessage, obj)}
      </div>
    );
  };

  const onDateInputChange = (inputValue, obj, minDate, maxDate) => {
    let alertMessage = '';
    if (moment(inputValue, 'YYYY-MM-DD', true).isValid()) {
      // Check the value is present in between min and maxDate
      /* istanbul ignore next */
      if (!moment(inputValue).isBetween(minDate, maxDate, 'day', '[]')) {
        alertMessage = `Date should be in between ${minDate} and ${maxDate}`;
      }
    } else {
      alertMessage = obj.props.validationMsg || '';
    }

    setFieldsData({
      ...fieldsData,
      [obj.name]: inputValue,
    });
    setValidationMessage({
      ...validationMessage,
      [`isValid${obj.name}`]: alertMessage,
    });
  };
  const renderDatePicker = (obj) => {
    const isEmptyOrValidMessage = validationMessage[`isValid${obj.name}`] === undefined || validationMessage[`isValid${obj.name}`] === '';
    return (
      <div>
        <Label className="margin-1-t" htmlFor={obj.name} id="dpd-label">
          {`${obj.props.label || obj.props.placeholder} `}
          {renderMandatory(obj)}
        </Label>
        {obj.props.tooltip && renderTooltip(obj)}
        <DatePicker
          id={obj.name}
          name={obj.name}
          date={fieldsData[obj.name] || ''}
          onDateChange={(e, date) => {
            onInputChange(date.dateString, obj);
            onDateInputChange(date.dateString, obj, getDateRange(obj.props.minDateRange, null, obj.props.minYear || null, 'mindate', false), getDateRange(obj.props.maxDateRange, null, obj.props.maxYear || null, 'maxdate', obj.props.hasFixedDate || false));
          }}
          minDate={getDateRange(obj.props.minDateRange, null, obj.props.minYear || null, 'mindate', false)}
          maxDate={getDateRange(obj.props.maxDateRange, null, obj.props.maxYear || null, 'maxdate', obj.props.hasFixedDate || false)}
          format={obj.props.mask || 'MM/DD/YYYY'}
          placeholder={obj.props.mask || null}
          locale={obj.props.locale || 'en-US'}
          // theme={{ '&': fluid, '&>div:first-child': fluid, 'input + button': dlsWhiteBgHvr }}
          status={isEmptyOrValidMessage && scrollIntoForm === null ? 'default' : 'error'}
          data-testid={`${obj.name}DatePicker`}
          value={fieldsData[obj.name]}
        />
        {renderAlert(isEmptyOrValidMessage, obj)}
      </div>
    );
  };

  const renderSelectOptions = (options) => options.map((option) => (
    <SelectOption key={`option${option.value}`} value={option.value}>{option.label}</SelectOption>
  ));

  const renderDropDown = (obj) => {
    const isEmptyOrValidMessage = validationMessage[`isValid${obj.name}`] === undefined || validationMessage[`isValid${obj.name}`] === '';
    return (
      <div>
        <Label className="margin-1-t" htmlFor={obj.name} id="dpd-label">
          {`${obj.props.label || obj.props.placeholder} `}
          {renderMandatory(obj)}
        </Label>
        {obj.props.tooltip && renderTooltip(obj)}
        <Select
          id={obj.name}
          name={obj.name}
          fluid={true}
          onChange={(e) => {
            onDropdownChange(e.target.value, obj.name, obj.props.mandatory === 'true', obj.props.validationMsg);
          }}
          status={isEmptyOrValidMessage && scrollIntoForm === null ? 'default' : 'error'}
          data-testid={`${obj.name}Select`}
          value={fieldsData[obj.name] ? fieldsData[obj.name] : ''}
        >
          {renderSelectOptions(obj.options)}
        </Select>
        {renderAlert(isEmptyOrValidMessage, obj)}
      </div>
    );
  };

  const renderFormFields = (obj) => {
    switch (obj.component) {
      case 'input':
        return renderInput(obj);
      case 'select':
        return renderDropDown(obj);
      case 'datepicker':
        return renderDatePicker(obj);
      default:
        return null;
    }
  };

  const renderForm = (fields) => fields.map((obj) => {
    const control = transformData(obj);
    return (
      <div key={`div${control.name}`}>
        {
          renderFormFields(control)
        }
      </div>
    );
  });

  if (formIsLoading !== undefined && formIsLoading) {
    return <div data-testid="formIsLoadingId" className="flex flex-align-center flex-justify-center pad-2-tb"><ProgressCircle size="lg" /></div>;
  }
  if (formDataError !== undefined && formDataError) {
    return <div>Error loading form.</div>;
  }
  return (
    <IntlProvider locale={locale} messages={languageData}>
      <div>
        <DefaultStyle />
        {(inacDocTypeId !== activeDocTypeId) && formData !== null && formData.body.fieldDa
          && (
          <div>
            {renderForm(formData.body.fieldDa)}
          </div>
          )}
      </div>
    </IntlProvider>
  );
}

DocUploadForm.defaultProps = {
  docTypeId: '',
  onFormChange: null,
  languageData: {},
  scrollIntoForm: null,
  locale: 'en-US',
  dynamicKey: '',
  optionalType: '',
};

DocUploadForm.propTypes = {
  docTypeId: PropTypes.string,
  onFormChange: PropTypes.func,
  languageData: PropTypes.instanceOf(Object),
  scrollIntoForm: PropTypes.string,
  locale: PropTypes.string,
  dynamicKey: PropTypes.string,
  optionalType: PropTypes.string,
};

export function mapStateToProps(state, ownProps) {
  const dynamicKey = ownProps.dynamicKey || '';
  const docTypeId = ownProps.docTypeId || '';
  return {
    dynamicKey,
    docTypeId,
  };
}

export const loadModuleData = async ({ store: { dispatch } }) => {
  await dispatch(loadLanguagePack('axp-doc-upload-form', { fallbackLocale: 'en-US' }));
};
DocUploadForm.holocron = {
  name: 'axp-doc-upload-form',
  loadModuleData,
};

const UnwrappedDocUploadForm = DocUploadForm;
export { UnwrappedDocUploadForm };

// export default hocChain(DocUploadForm);
export default compose(
  oneAppModuleWrapper('axp-doc-upload-form'),
  connect(mapStateToProps)
)(DocUploadForm);
